/**
 * KAN-755: поля параметров скидки по реестру типа (GET /discount-types/).
 * Виджет выбирается картой "тип поля -> виджет": percent, money, date,
 * string (верхний регистр, промокод), tiers (TiersEditor). Неизвестный тип:
 * текстовое поле.
 */
import { FormField } from '@/components/catalog/FormField';
import { Input } from '@/components/ui/input';
import type { FormErrors, ParamValue, TierRow } from '@/lib/discountForm';
import type { DiscountParamSpec } from '@/types/discount';
import { TiersEditor } from './TiersEditor';

interface WidgetProps {
  id: string;
  spec: DiscountParamSpec;
  value: ParamValue;
  onChange: (value: ParamValue) => void;
  disabled: boolean;
  invalid: boolean;
}

const asText = (value: ParamValue): string => (typeof value === 'string' ? value : '');

function textWidget(inputMode: 'decimal' | 'text', suffix?: string, transform?: (raw: string) => string) {
  return function TextWidget({ id, spec, value, onChange, disabled, invalid }: WidgetProps) {
    return (
      <div className="flex items-center gap-2">
        <Input
          id={id}
          inputMode={inputMode}
          autoComplete="off"
          className={suffix ? 'w-40' : undefined}
          value={asText(value)}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-label={spec.label}
          onChange={(e) => onChange(transform ? transform(e.target.value) : e.target.value)}
        />
        {suffix && <span className="text-sm text-gray-600">{suffix}</span>}
      </div>
    );
  };
}

function DateWidget({ id, spec, value, onChange, disabled, invalid }: WidgetProps) {
  return (
    <Input
      id={id}
      type="date"
      className="w-48"
      value={asText(value)}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      aria-label={spec.label}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function TiersWidget({ id, value, onChange, disabled, invalid }: WidgetProps) {
  return (
    <TiersEditor
      id={id}
      value={Array.isArray(value) ? (value as TierRow[]) : []}
      onChange={onChange}
      disabled={disabled}
      invalid={invalid}
    />
  );
}

const PercentWidget = textWidget('decimal', '%');
const MoneyWidget = textWidget('decimal', '₽');
const UpperWidget = textWidget('text', undefined, (raw) => raw.toUpperCase());
const PlainWidget = textWidget('text');

const PARAM_WIDGETS: Record<string, (props: WidgetProps) => JSX.Element> = {
  percent: PercentWidget,
  money: MoneyWidget,
  date: DateWidget,
  string: UpperWidget,
  tiers: TiersWidget,
};

interface DiscountParamsFieldsProps {
  specs: DiscountParamSpec[];
  values: Record<string, ParamValue>;
  onChange: (name: string, value: ParamValue) => void;
  errors: FormErrors;
  disabled?: boolean;
}

export function DiscountParamsFields({ specs, values, onChange, errors, disabled = false }: DiscountParamsFieldsProps) {
  return (
    <div className="flex flex-col gap-3" data-testid="discount-params">
      {specs.map((spec) => {
        const id = `discount-param-${spec.name}`;
        const error = errors[`params.${spec.name}`];
        const Widget = PARAM_WIDGETS[spec.type] ?? PlainWidget;
        return (
          <FormField
            key={spec.name}
            id={id}
            label={spec.required ? `${spec.label} *` : spec.label}
            error={error}
            hint={spec.help ?? undefined}
          >
            <Widget
              id={id}
              spec={spec}
              value={values[spec.name] ?? (spec.type === 'tiers' ? [] : '')}
              onChange={(value) => onChange(spec.name, value)}
              disabled={disabled}
              invalid={!!error}
            />
          </FormField>
        );
      })}
      {errors.params && (
        <p className="text-xs text-red-700" data-testid="discount-params-error">
          {errors.params}
        </p>
      )}
    </div>
  );
}
